import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Map } from 'immutable';
import classNames from 'classnames';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/nl';
import { ImageFromField } from '@uncinc/react-drupal-core';

import {
  Container,
  ScrollIndicator,
  Share,
  WysiwygField,
} from '../../../../components';

import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  SCREEN_WIDE,
} from '../../../../config/constants';

import gtm from '../../../../helpers/gtm';

import Video from '../../../../components/Video';
import useVacancyApplication from '../../../../helpers/useVacancyApplication';
import useWhatsappLink from '../../../../helpers/useWhatsappLink';
import NodeVacancyDetails from './details';
import NodeVacancyFullBlockContact from './BlockContact';

import './style.scss';

dayjs.extend(LocalizedFormat);

const NodeVacancyFull = ({ entity, className }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);

  const classes = classNames(className, 'Vacancy');
  const path = entity.getIn(['paths', 'nl'], '');
  const title = entity.getIn(['title', 0, 'value']);
  const headerBackground = entity.getIn(['field_header_background', 0], Map());

  const { handleApply } = useVacancyApplication(entity.toJS());

  // Block contact.
  const whatsappContact = entity.getIn(['field_whatsapp_contact', 0, 'value'], '');
  const whatsappNumber = entity.getIn(['field_whatsapp_number', 0, 'value'], '');
  const whatsappEmail = entity.getIn(['field_contact_email', 0, 'value'], '');
  const showBlockContact = whatsappContact && (whatsappNumber || whatsappEmail);

  const whatsappLink = useWhatsappLink(
    whatsappNumber && whatsappNumber,
    whatsappContact && whatsappContact,
  );

  return (
    <>
      <ScrollIndicator scrollAreaRef={wrapperRef} />
      <article className={classes} ref={wrapperRef}>
        <div className="Vacancy--hero">
          {headerBackground.size > 0 && (
            <ImageFromField
              field={headerBackground}
              imageStyles={Map({
                [SCREEN_MOBILE]: 'cropped_hero_small_small_viewport',
                [SCREEN_TABLET]: 'cropped_hero_medium_viewport',
                [SCREEN_NORMAL]: 'cropped_hero_medium_viewport',
                [SCREEN_WIDE]: 'crop_hero',
              })}
              lazyload
            />
          )}
        </div>

        <Container>
          <NodeVacancyDetails
            entity={entity}
            whatsappLink={whatsappLink}
            onVacancyApply={() => handleApply()}
          />

          {entity.getIn(['body', 0, 'processed']) ? (
            <div id="vacancy-body" className="u-indent--large u-margin--top--9">
              <h2>Vacature</h2>
              <WysiwygField
                property="schema:text"
                className="node--body"
                html={entity.getIn(['body', 0, 'processed'], '')}
              />
            </div>
          ) : null}

          {entity.getIn(['field_video_url', 0, 'value'], null) && (
            <div className="u-indent--large u-margin--top--9">
              <Video
                controls
                playsInline
                sources={[
                  {
                    src: entity.getIn(['field_video_url', 0, 'value'], null),
                    type: 'video/youtube',
                  },
                ]} />
            </div>
          )}

          {showBlockContact && (
            <div className="u-indent--large u-margin--top--9">
              <NodeVacancyFullBlockContact
                name={whatsappContact}
                tel={whatsappNumber}
                email={whatsappEmail}
                whatsappLink={whatsappLink}
                onVacancyApply={() => handleApply()}
              />
            </div>
          )}

          <div className="u-indent--large">
            <Share
              items={[
                'facebook',
                'linkedin',
                'twitter',
              ]}
              onClick={(medium) => {
                gtm.trackUserEvent('social-share', 'click', medium, path);
              }}
              pageTitle={title}
              title={t('share')}
              url={`https://www.werkenbijhvc.nl${path}`}
            />
          </div>
        </Container>
      </article>
    </>
  );
};

export default NodeVacancyFull;
